<template>
    <div>
        <div class="px-5">
            <div class="row">
                <div class="col-md-12 mb-3">
                    <h2 class="text-center">
                        <template v-if="!surveysEnabled">
                            Massutskick
                        </template>
                        <template v-else> Enkätutskick </template>
                    </h2>
                </div>

                <div class="col-md-12">
                    <div class="card mb-4">
                        <div class="card-header">Meddelande</div>
                        <div class="card-body">
                            <div v-if="type != 'none'" class="input-group mb-3">
                                <div class="input-group-prepend">
                                    <div class="input-group-text">
                                        Meddelande
                                    </div>
                                </div>
                                <textarea
                                    class="form-control"
                                    rows="6"
                                    v-model="message"
                                ></textarea>
                            </div>

                            <template v-if="surveysEnabled">
                                <div class="input-group mb-3">
                                    <div class="input-group-prepend">
                                        <div class="input-group-text">
                                            Bifoga enkät
                                        </div>
                                    </div>
                                    <select
                                        class="custom-select"
                                        v-model="attachedSurveyId"
                                        required
                                    >
                                        <option :value="null" selected>
                                            Välj...
                                        </option>
                                        <option
                                            v-for="survey in surveys"
                                            :key="survey.id"
                                            :value="survey.id"
                                        >
                                            {{ survey.name }}
                                        </option>
                                    </select>
                                </div>

                                <div
                                    v-if="attachedSurveyId"
                                    class="input-group mb-3"
                                >
                                    <div class="input-group-prepend">
                                        <div class="input-group-text">
                                            Enkät måste vara inskickad
                                        </div>
                                    </div>
                                    <input
                                        class="form-control"
                                        type="date"
                                        v-model="attachedSurveyRequiredAt"
                                        :min="surveyRequiredMinDate"
                                        :max="surveyRequiredMaxDate"
                                    >
                                </div>
                            </template>

                            <div class="mb-3 text-center">
                                <div class="form-check form-check-inline">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        :id="`${uuid}-type-sms`"
                                        value="sms"
                                        v-model="type"
                                    />
                                    <label
                                        class="form-check-label"
                                        :for="`${uuid}-type-sms`"
                                        >SMS</label
                                    >
                                </div>

                                <div class="form-check form-check-inline">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        :id="`${uuid}-type-email`"
                                        value="email"
                                        v-model="type"
                                    />
                                    <label
                                        class="form-check-label"
                                        :for="`${uuid}-type-email`"
                                        >e-post</label
                                    >
                                </div>

                                <div
                                    v-if="surveysEnabled"
                                    class="form-check form-check-inline"
                                >
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        :id="`${uuid}-type-none`"
                                        value="none"
                                        v-model="type"
                                    />
                                    <label
                                        class="form-check-label"
                                        :for="`${uuid}-type-none`"
                                        >inget utskick</label
                                    >
                                </div>
                            </div>

                            <p
                                v-if="type === 'sms'"
                                class="mb-3 text-center font-italic"
                            >
                                Debiteringsmässigt är alltid ett SMS 160 tecken.
                                Emojis kan innebära flera tecken per styck.
                            </p>

                            <div
                                v-if="!submitting"
                                class="btn-toolbar justify-content-center"
                            >
                                <button
                                    type="button"
                                    class="btn btn-secondary mr-1"
                                    :disabled="submitDisabled"
                                    @click.prevent="submit(true)"
                                >
                                    Skicka till dig själv
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-primary ml-1 mr-1"
                                    :disabled="submitDisabled"
                                    @click.prevent="submit(false)"
                                >
                                    Skicka till {{ quoteCount }} kunder
                                </button>

                                <button
                                    type="button"
                                    class="btn btn-secondary ml-1"
                                    data-toggle="collapse"
                                    href="#tagInstructions"
                                    aria-expanded="false"
                                    aria-controls="#tagInstructions"
                                >
                                    Läs mer / avancerat
                                </button>
                            </div>
                            <div v-else class="text-center">
                                <SbSpinner label="Skickar.."></SbSpinner>
                            </div>

                            <SbAlert
                                v-if="sendSuccess"
                                class="mt-5"
                                variant="success"
                                show
                                >Ditt meddelande har skickats ut!</SbAlert
                            >
                            <SbAlert
                                v-if="sendError"
                                class="mt-5"
                                variant="danger"
                                show
                                >Ett fel uppstod när meddelandet skickades ut,
                                och vissa meddelanden kanske inte kunde
                                eventuellt inte levereras. Kontakta SalesByte
                                support för mer hjälp.</SbAlert
                            >
                        </div>
                    </div>

                    <div class="collapse" id="tagInstructions">
                        <div class="card mb-4">
                            <div class="card-body">
                                <slot name="tag-instructions"></slot>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <data-analysis-quotes-selector
            @filterConfig="onFilterConfig"
            @quoteCount="onQuoteCount"
            :base-filter-config="baseFilterConfig"
            show-contact-methods
        />
    </div>
</template>

<style lang="sass" scoped></style>

<script setup>
import { route } from "ziggy";
import { computed, onMounted, ref } from "vue";
import { modalController } from "../sb/controllers";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
const props = defineProps({
    surveysEnabled: {
        type: Boolean,
        default: false,
    },
    surveyRequiredMinDate: {
        type: String,
        default: null,
    },
    surveyRequiredMaxDate: {
        type: String,
        default: null,
    },
});
const uuid = ref(uuidv4());
const type = ref("sms");
const message = ref("");
const submitting = ref(false);
const sendSuccess = ref(false);
const sendError = ref(false);
const quoteCount = ref(0);
const filterConfig = ref({});
const surveys = ref([]);
const attachedSurveyId = ref(null);
const attachedSurveyRequiredAt = ref(null);
const submitDisabled = computed(() => {
    return (
        !quoteCount.value ||
        quoteCount.value < 1 ||
        (type.value != "none" && !message.value) ||
        !type.value
    );
});
const baseFilterConfig = computed(() => {
    const config = {};
    if (type.value == "sms" && !attachedSurveyId.value) {
        config.has_customer_phone = true;
    }
    if (type.value == "email" && !attachedSurveyId.value) {
        config.has_customer_email = true;
    }
    return {
        include: config,
        exclude: {},
    };
});
const filterQuoteFunc = computed(() => {
    return buildFilterQuoteFunc(false);
});
function buildFilterQuoteFunc(ignoreSurvey) {
    if (type.value == "sms" && (!attachedSurveyId.value || ignoreSurvey)) {
        return (quote) => !!quote.customer_phone;
    }
    if (type.value == "email" && (!attachedSurveyId.value || ignoreSurvey)) {
        return (quote) => !!quote.customer_email;
    }
    return (_quote) => true;
}
async function loadSurveys() {
    const loadedSurveys = [];
    let nextUrl = route("surveys.index");
    while (nextUrl) {
        const response = await axios.get(nextUrl);
        loadedSurveys.push(...response.data.data);
        nextUrl = response.data.links.next;
    }
    surveys.value = loadedSurveys.sort((a, b) => a.name.localeCompare(b.name));
}
async function submit(test) {
    if (submitting.value) {
        return;
    }
    submitting.value = true;
    sendSuccess.value = false;
    sendError.value = false;
    if (!test) {
        const confirmMsg = `Är du säker på att du vill skicka till ${quoteCount.value} kunder?`;
        const confirmed = await modalController.msgBoxConfirm(confirmMsg, {
            okTitle: "Skicka",
            cancelTitle: "Avbryt",
            centered: true,
        });
        if (confirmed !== true) {
            submitting.value = false;
            return;
        }
    }
    try {
        let nextQuoteUrl = route("data_analysis.quotes.index", {
            _query: {
                per_page: 10,
                filter_config: filterConfig.value,
            },
        });
        while (nextQuoteUrl) {
            const response = await axios.get(nextQuoteUrl);
            nextQuoteUrl = response.data.links.next;
            const quotes = response.data.data;
            await axios.post(route("quote_messages.store"), {
                quote_ids: quotes.map((q) => q.id),
                message: message.value,
                type: type.value,
                test: test,
                attached_survey_id: attachedSurveyId.value,
                attached_survey_required_at: attachedSurveyRequiredAt.value,
            });

            // Only run first batch if test
            if (test) {
                break;
            }
        }
        sendSuccess.value = true;
    } catch (err) {
        sendError.value = true;
        console.warn(err);
    } finally {
        submitting.value = false;
    }
}
function onFilterConfig(value) {
    filterConfig.value = value;
}
function onQuoteCount(value) {
    quoteCount.value = value;
}
onMounted(async () => {
    if (props.surveysEnabled) {
        await loadSurveys();
    }
});
</script>
